import { useState } from 'react';
import { Check, Clock, Loader2, MapPin, User, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/utils';
import { useCustomerDetailContext } from '@/pages/admin/AdminCustomerDetail';
import { SupersededOrders } from '@/pages/admin/customer-detail/SupersededOrdersPanel';
import {
  useAdminAccountChangeRequests,
  useReviewAccountChange,
  type AdminAccountChangeRequest,
} from '@/hooks/admin/useAccountChangeRequestsAdmin';

/** Friendly labels for the proposed_values keys, by kind. */
const FIELD_LABELS: Record<string, string> = {
  line1: 'Street address',
  line2: 'Address line 2',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
  delivery_notes: 'Delivery notes',
  company_name: 'Company name',
  billing_contact_name: 'Billing contact',
  billing_email: 'Billing email',
};

/**
 * Two things live on this tab, and they are different work:
 *
 *  - ACCOUNT changes (address / contact) — we APPLY them here, in the portal.
 *  - CHANGED ORDERS — a customer changing an order places a real replacement
 *    order; what is left over is deleting the old Service Fusion job, which
 *    only a person can do because SF has no write API for jobs. That panel is
 *    read-only and clears itself.
 *
 * ⚠ The account-change section early-returns on empty/loading, so the changed-
 * orders panel is rendered by the wrapper ABOVE it — otherwise a customer with
 * no account-change requests (the common case) would never see it at all.
 */
export default function ChangeRequestsTab() {
  const { customer } = useCustomerDetailContext();

  return (
    <div className="space-y-6">
      <SupersededOrders customerId={customer.id} customerName={customer.name} />
      <AccountChangesSection />
    </div>
  );
}

function AccountChangesSection() {
  const { customer } = useCustomerDetailContext();
  const { data: requests, isLoading, error } = useAdminAccountChangeRequests(customer.id);

  if (isLoading) {
    return (
      <div className="px-2 py-10 text-center text-sm text-brix-muted">
        <Loader2 size={14} className="animate-spin inline-block mr-2" />
        Loading change requests…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-brix-warning/30 bg-brix-warning/5 px-4 py-3 text-sm text-brix-warning">
        {error instanceof Error ? error.message : 'Could not load change requests.'}
      </div>
    );
  }

  const rows = requests ?? [];
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-8 text-center">
        <p className="text-sm text-brix-muted">
          No account-info change requests from {customer.name} yet.
        </p>
        <p className="text-[11px] text-brix-muted mt-1">
          When a customer proposes an address or contact change from their Settings page, it lands
          here for review.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-sm text-brix-muted">
        Proposed account-info changes from {customer.name}. Applying a request writes the new values
        into the customer's account; rejecting leaves everything unchanged. Nothing is pushed to
        Service Fusion or QuickBooks.
      </p>
      {rows.map((req) => (
        <RequestCard key={req.id} req={req} customerId={customer.id} />
      ))}
    </div>
  );
}

function RequestCard({ req, customerId }: { req: AdminAccountChangeRequest; customerId: string }) {
  const review = useReviewAccountChange();
  const [showReject, setShowReject] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  const pending = req.status === 'pending';
  const applying = review.isPending && review.variables?.action === 'apply';
  const rejecting = review.isPending && review.variables?.action === 'reject';
  const errMsg = review.error instanceof Error ? review.error.message : null;

  // The set of fields the customer proposed.
  const keys = Object.keys(req.proposedValues);

  const onApply = () => {
    if (
      !window.confirm(
        `Apply this ${req.kind === 'address' ? 'address' : 'contact'} change to the customer's account?`,
      )
    ) {
      return;
    }
    review.mutate({ requestId: req.id, action: 'apply', customerId });
  };

  const onReject = () => {
    review.mutate(
      {
        requestId: req.id,
        action: 'reject',
        review_note: rejectNote.trim() || undefined,
        customerId,
      },
      { onSuccess: () => setShowReject(false) },
    );
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {req.kind === 'address' ? (
            <MapPin size={14} className="text-brix-primary shrink-0" />
          ) : (
            <User size={14} className="text-brix-primary shrink-0" />
          )}
          <span className="text-sm font-semibold text-brix-ink">
            {req.kind === 'address' ? 'Delivery / billing address' : 'Company contact info'}
          </span>
        </div>
        <StatusPill status={req.status} />
      </div>

      <div className="text-[11px] text-brix-muted">
        Requested {new Date(req.createdAt).toLocaleString()}
        {req.requestedByEmail ? ` by ${req.requestedByEmail}` : ''}
      </div>

      {/* current → proposed diff */}
      <div className="rounded-lg border border-brix-border overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-0 text-xs">
          <div className="bg-brix-surface-alt/60 px-3 py-1.5 font-semibold text-brix-muted">
            Current
          </div>
          <div className="bg-brix-surface-alt/60 px-2 py-1.5" />
          <div className="bg-brix-surface-alt/60 px-3 py-1.5 font-semibold text-brix-primary">
            Proposed
          </div>
          {keys.map((k) => {
            const current = req.currentValues?.[k] ?? null;
            const proposed = req.proposedValues[k] ?? null;
            const changed = (current ?? '') !== (proposed ?? '');
            return (
              <DiffRow
                key={k}
                label={FIELD_LABELS[k] ?? k}
                current={current}
                proposed={proposed}
                changed={changed}
              />
            );
          })}
        </div>
      </div>

      {req.note && (
        <div className="text-xs text-brix-ink">
          <span className="font-semibold text-brix-muted">Customer note: </span>
          {req.note}
        </div>
      )}

      {!pending && req.reviewNote && (
        <div className="text-xs text-brix-muted italic">
          Review note: {req.reviewNote}
          {req.reviewedByEmail ? ` — ${req.reviewedByEmail}` : ''}
        </div>
      )}

      {errMsg && <div className="text-xs text-red-600">{errMsg}</div>}

      {pending && !showReject && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Button size="sm" onClick={onApply} disabled={review.isPending}>
            {applying ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {applying ? 'Applying…' : 'Apply'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-700 hover:bg-red-50"
            onClick={() => setShowReject(true)}
            disabled={review.isPending}
          >
            <X size={12} /> Reject
          </Button>
        </div>
      )}

      {pending && showReject && (
        <div className="space-y-2 pt-1">
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Optional note to record why this was rejected"
            rows={2}
            className="w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-xs text-brix-ink placeholder:text-brix-muted focus:outline-none focus:ring-2 focus:ring-brix-primary/40"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-red-700 hover:bg-red-50"
              onClick={onReject}
              disabled={review.isPending}
            >
              {rejecting ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
              {rejecting ? 'Rejecting…' : 'Confirm reject'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowReject(false)}
              disabled={review.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DiffRow({
  label,
  current,
  proposed,
  changed,
}: {
  label: string;
  current: string | null;
  proposed: string | null;
  changed: boolean;
}) {
  return (
    <>
      <div className="px-3 py-1.5 border-t border-brix-border">
        <div className="text-[10px] uppercase tracking-tight text-brix-muted">{label}</div>
        <div className="text-brix-ink break-words">
          {current || <span className="italic text-brix-muted">empty</span>}
        </div>
      </div>
      <div className="px-2 py-1.5 border-t border-brix-border flex items-center text-brix-muted">
        →
      </div>
      <div
        className={cn(
          'px-3 py-1.5 border-t border-brix-border',
          changed ? 'bg-emerald-50/50' : '',
        )}
      >
        <div className="text-[10px] uppercase tracking-tight text-brix-muted">{label}</div>
        <div className={cn('break-words', changed ? 'font-semibold text-emerald-800' : 'text-brix-ink')}>
          {proposed || <span className="italic text-brix-muted">empty</span>}
        </div>
      </div>
    </>
  );
}

function StatusPill({ status }: { status: AdminAccountChangeRequest['status'] }) {
  if (status === 'pending')
    return (
      <StatusBadge tone="warning" size="sm" icon={<Clock size={11} />}>
        Pending
      </StatusBadge>
    );
  if (status === 'applied')
    return (
      <StatusBadge tone="success" size="sm" icon={<Check size={11} />}>
        Applied
      </StatusBadge>
    );
  return (
    <StatusBadge tone="neutral" size="sm" icon={<X size={11} />}>
      Rejected
    </StatusBadge>
  );
}
